import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { cn } from "../../utils/tailwindUtils";
import {
    ArrowLeft, RefreshCw, Loader2, MailOpen, MousePointerClick,
    AlertTriangle, Send, XCircle,
} from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import TruncatedText from "../../components/ui/truncated-text";
import { EMAIL_CAMPAIGNS_URL, EMAIL_CAMPAIGN_RECIPIENTS_URL } from "../../constants/api";

const pct = (num, den) => den > 0 ? `${Math.round(num / den * 100)}%` : '—';

const ROW_STATUS = {
    sent:    'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
    queued:  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    failed:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    skipped: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
};

const Stat = ({ icon: Icon, label, value, sub, tone }) => (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3">
        <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        <div className={cn("mt-1 text-xl font-semibold", tone || "text-gray-800 dark:text-gray-100")}>
            {value}
        </div>
        {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
);

export default function EmailCampaignReport() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { fetch } = useApiMethods();

    const [campaign, setCampaign] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [c, r] = await Promise.all([
                fetch(`${EMAIL_CAMPAIGNS_URL}?id=${id}`),
                fetch(EMAIL_CAMPAIGN_RECIPIENTS_URL(id) + (filter ? `?status=${filter}` : '')),
            ]);
            if (c?.status === 'success') setCampaign(c.data);
            else ToastMsg("error", c?.message || "Failed to load campaign");
            if (r?.status === 'success') setRows(r.data || []);
        } catch {
            ToastMsg("error", "Failed to load campaign");
        }
        setLoading(false);
    }, [id, filter, fetch]);

    useEffect(() => { load(); }, [id, filter]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!campaign && loading) {
        return (
            <div className="p-6"><ContainerCard>
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
            </ContainerCard></div>
        );
    }
    if (!campaign) {
        return (
            <div className="p-6"><ContainerCard>
                <p className="py-12 text-center text-sm text-gray-400">Campaign not found.</p>
            </ContainerCard></div>
        );
    }

    const sent = campaign.sent_count || 0;

    return (
        <div className="p-6 space-y-6">
            <ContainerCard>
                <div className="flex items-start justify-between mb-6">
                    <div className="flex items-start gap-3">
                        <button onClick={() => navigate('/email/campaigns')}
                                className="mt-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                            <ArrowLeft className="h-4 w-4" />
                        </button>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">{campaign.name}</h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                                {campaign.subject || '—'} · from {campaign.account_name || '—'}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                                Status <span className="font-medium">{campaign.status}</span>
                                {campaign.started_at && ` · started ${new Date(campaign.started_at).toLocaleString()}`}
                                {campaign.completed_at && ` · finished ${new Date(campaign.completed_at).toLocaleString()}`}
                            </p>
                        </div>
                    </div>
                    <Button variant="outline" size="sm" onClick={load} disabled={loading}
                            className="flex items-center gap-1 text-xs">
                        <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} /> Refresh
                    </Button>
                </div>

                {campaign.last_error && (
                    <div className="mb-4 flex gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                        <span>{campaign.last_error}</span>
                    </div>
                )}

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 mb-6">
                    <Stat icon={Send} label="Sent" value={sent}
                          sub={`of ${campaign.total_recipients} recipients`} />
                    <Stat icon={MailOpen} label="Opened" value={campaign.opened_count}
                          sub={pct(campaign.opened_count, sent)} />
                    <Stat icon={MousePointerClick} label="Clicked" value={campaign.clicked_count}
                          sub={pct(campaign.clicked_count, sent)} />
                    <Stat icon={AlertTriangle} label="Bounced" value={campaign.bounced_count}
                          sub={pct(campaign.bounced_count, sent)}
                          tone="text-amber-600 dark:text-amber-500" />
                    <Stat icon={XCircle} label="Failed" value={campaign.failed_count}
                          tone="text-red-500" />
                </div>

                <div className="flex items-center gap-2 mb-3">
                    {['', 'sent', 'queued', 'failed', 'skipped'].map(s => (
                        <button key={s || 'all'} onClick={() => setFilter(s)}
                                className={cn(
                                    "rounded-full px-3 py-1 text-[11px] font-medium transition-colors",
                                    filter === s
                                        ? "bg-blue-600 text-white"
                                        : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                                )}>
                            {s || 'all'}
                        </button>
                    ))}
                    <span className="ml-auto text-xs text-gray-400">{rows.length} shown</span>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                <th className="pb-2 font-medium">Recipient</th>
                                <th className="pb-2 font-medium">Email</th>
                                <th className="pb-2 font-medium">Status</th>
                                <th className="pb-2 font-medium">Opens</th>
                                <th className="pb-2 font-medium">Clicks</th>
                                <th className="pb-2 font-medium">Sent at</th>
                                <th className="pb-2 font-medium">Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                    <td className="py-2.5 max-w-[180px] font-medium text-gray-800 dark:text-gray-100">
                                        <TruncatedText text={r.name} />
                                    </td>
                                    <td className="py-2.5 max-w-[220px] text-gray-600 dark:text-gray-400">
                                        <TruncatedText text={r.email} />
                                    </td>
                                    <td className="py-2.5">
                                        <span className={cn(
                                            'inline-block rounded-full px-2 py-0.5 text-[11px] font-medium',
                                            ROW_STATUS[r.status] || ROW_STATUS.queued
                                        )}>{r.status}</span>
                                    </td>
                                    <td className={cn("py-2.5", r.is_opened ? "text-teal-600 dark:text-teal-400" : "text-gray-400")}>
                                        {r.open_count || 0}
                                    </td>
                                    <td className={cn("py-2.5", r.is_clicked ? "text-blue-600 dark:text-blue-400" : "text-gray-400")}>
                                        {r.click_count || 0}
                                    </td>
                                    <td className="py-2.5 text-xs text-gray-500">
                                        {r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}
                                    </td>
                                    <td className="py-2.5 max-w-[200px] text-xs text-red-500">
                                        <TruncatedText text={r.failure_reason} label="Failure" />
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="py-8 text-center text-sm text-gray-400">
                                        {campaign.status === 'draft'
                                            ? 'Recipients are resolved when the campaign starts.'
                                            : 'No recipients match this filter.'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </ContainerCard>
        </div>
    );
}
